import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ImportantNotesBanner from '../components/ImportantNotesBanner';
import ImportantNotesButton from '../components/ImportantNotesButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogTitle,
} from '../components/ui/dialog';
import {
    ArrowLeft, Clock, User, FileText, MapPin, Plus, RefreshCw,
    Trash2, ChevronDown, ChevronUp, DollarSign, X, Package, Gift, Layers,
    MoreHorizontal, Eye, Pencil, Download, Send, CheckCircle2, XCircle, Mail, PartyPopper, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useResilientSave, isNetworkError } from '../components/OfflineSavePrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// "8. Prix final" section UI accepts TTC values. The backend stores
// prix_prestation and prestations[].prix as HT, so we translate at the
// boundary. round2 keeps centime precision and avoids floating noise.
const TVA_RATE = 0.20;
const TTC_FACTOR = 1 + TVA_RATE; // 1.20
const round2 = (n) => Math.round(n * 100) / 100;
const htToTtc = (v) => (v === '' || v === null || v === undefined ? '' : String(round2((parseFloat(v) || 0) * TTC_FACTOR)));
const ttcToHt = (v) => (v === '' || v === null || v === undefined ? '' : String(round2((parseFloat(v) || 0) / TTC_FACTOR)));

const STATUT_CONFIG = {
    a_envoyer: { label: 'A envoyer', color: 'bg-amber-100 text-amber-700 border-amber-300', hint: 'Envoyez le devis pour modifier le statut', borderColor: 'border-amber-300', dot: 'bg-amber-500' },
    envoye: { label: 'Envoyer', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', hint: '', borderColor: 'border-emerald-400', dot: 'bg-emerald-500' },
    relance_1: { label: 'Relance 1', color: 'bg-orange-100 text-orange-700 border-orange-300', hint: '', borderColor: 'border-orange-400', dot: 'bg-orange-500' },
    relance_2: { label: 'Relance 2', color: 'bg-orange-100 text-orange-700 border-orange-300', hint: '', borderColor: 'border-orange-400', dot: 'bg-orange-500' },
    relance_3: { label: 'Relance 3', color: 'bg-orange-100 text-orange-700 border-orange-300', hint: '', borderColor: 'border-orange-400', dot: 'bg-orange-500' },
    relance_whatsapp: { label: 'Relance WhatsApp', color: 'bg-green-100 text-green-700 border-green-300', hint: '', borderColor: 'border-green-400', dot: 'bg-green-500' },
    decision_pas_prise: { label: 'Decision pas prise', color: 'bg-gray-100 text-gray-700 border-gray-300', hint: '', borderColor: 'border-gray-400', dot: 'bg-gray-500' },
    interesse: { label: 'Interesse', color: 'bg-blue-100 text-blue-700 border-blue-300', hint: '', borderColor: 'border-blue-400', dot: 'bg-blue-500' },
    refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700 border-red-300', hint: '', borderColor: 'border-red-400', dot: 'bg-red-500' },
    accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', hint: '', borderColor: 'border-emerald-400', dot: 'bg-emerald-600' },
};

const FORMULE_LABELS = {
    economique: 'Economique',
    economique_plus: 'Economique Plus',
    standard: 'Standard',
    luxe: 'Luxe',
};

const PRESTATION_OPTIONS = [
    'Garde meuble',
    'Autorisation stationnement',
    'Monte meuble',
    'Dechetterie',
];

/* ========== FIELD ROW COMPONENT ========== */
function FieldRow({ label, required, children }) {
    return (
        <div className="flex items-center py-3">
            <div className="flex items-center gap-0 w-1/2">
                <div className={`w-0.5 h-6 rounded-full mr-3 ${required ? 'bg-red-400' : 'bg-gray-200'}`} />
                <span className="text-sm text-gray-700">{label}{required && ' *'}</span>
            </div>
            <div className="w-1/2">{children}</div>
        </div>
    );
}

/* ========== DEVIS FORMULE MODAL (create/view/edit) ========== */
function DevisFormuleModal({ open, onClose, formule, prospect, devisId, volumeItems, mode, existingData, existingId, otherFormules = [] }) {
    // mode: 'create' | 'view' | 'edit'
    const isReadonly = mode === 'view';
    const isEdit = mode === 'edit';
    const label = FORMULE_LABELS[formule] || formule;
    const title = mode === 'view' ? `Devis – ${label}` : mode === 'edit' ? `Modifier devis – ${label}` : `Creer un devis – ${label}`;
    const subtitle = mode === 'view' ? 'Details du devis' : mode === 'edit' ? 'Modifiez les informations du devis' : 'Remplissez les informations du devis';
    const adresseDepart = prospect?.adresse_depart || {};
    const adresseArrivee = prospect?.adresse_arrivee || {};

    const [form, setForm] = useState({
        nom: '', telephone: '', email: '',
        date_chargement: '', adresse_depart: '', etage_depart: '', portage_depart: '', aot_depart: '', monte_meuble_depart: '', ascenseur_depart: '',
        date_dechargement: '', adresse_arrivee: '', etage_arrivee: '', portage_arrivee: '', aot_arrivee: '', monte_meuble_arrivee: '', ascenseur_arrivee: '',
        garde_meuble: '', garde_meuble_mensualite: '', garde_meuble_duree: '',
        fournitures: '', cartons_standards: '', cartons_livres: '', cartons_verres: '', cartons_bouteilles: '',
        adhesif: '', bulle_100m: '', bulle_10m: '', autre_fourniture: '',
        remarques: '',
        volume_override: '',
        prix_prestation: '',
        prestations: [],
    });

    const [showPrestationMenu, setShowPrestationMenu] = useState(false);
    const [showVolumeDetails, setShowVolumeDetails] = useState(false);

    // Admin settings (fournitures prices + tarif_km) — needed to compute
    // "Total HT / TTC" for section 7b exactly like ProspectForm does.
    const [settingsTarifs, setSettingsTarifs] = useState({ fournitures: {}, tarif_km: 0 });
    useEffect(() => {
        if (!open) return;
        axios.get(`${API_URL}/api/settings`, { withCredentials: true })
            .then(res => {
                // The API returns prices nested under `tarifs`, exactly like
                // ProspectForm reads them. Without the `.tarifs` accessor,
                // every price ends up as 0 → fournitures cost & distance
                // cost silently disappear from the recap.
                const t = res.data?.tarifs || {};
                setSettingsTarifs({
                    fournitures: t.fournitures || {},
                    tarif_km: parseFloat(t.tarif_km) || 0,
                });
            })
            .catch(() => {});
    }, [open]);

    // Pre-fill from prospect (create) or existingData (view/edit)
    useEffect(() => {
        if (!open) return;
        if ((mode === 'view' || mode === 'edit') && existingData) {
            setForm({
                nom: existingData.nom || '',
                telephone: existingData.telephone || '',
                email: existingData.email || '',
                date_chargement: existingData.date_chargement || '',
                adresse_depart: existingData.adresse_depart || '',
                etage_depart: existingData.etage_depart || '',
                portage_depart: existingData.portage_depart || '',
                aot_depart: existingData.aot_depart || '',
                monte_meuble_depart: existingData.monte_meuble_depart || '',
                ascenseur_depart: existingData.ascenseur_depart || '',
                date_dechargement: existingData.date_dechargement || '',
                adresse_arrivee: existingData.adresse_arrivee || '',
                etage_arrivee: existingData.etage_arrivee || '',
                portage_arrivee: existingData.portage_arrivee || '',
                aot_arrivee: existingData.aot_arrivee || '',
                monte_meuble_arrivee: existingData.monte_meuble_arrivee || '',
                ascenseur_arrivee: existingData.ascenseur_arrivee || '',
                garde_meuble: existingData.garde_meuble || '',
                garde_meuble_mensualite: existingData.garde_meuble_mensualite || '',
                garde_meuble_duree: existingData.garde_meuble_duree || '',
                fournitures: existingData.fournitures || '',
                cartons_standards: existingData.cartons_standards || '',
                cartons_livres: existingData.cartons_livres || '',
                cartons_verres: existingData.cartons_verres || '',
                cartons_bouteilles: existingData.cartons_bouteilles || '',
                adhesif: existingData.adhesif || '',
                bulle_100m: existingData.bulle_100m || '',
                bulle_10m: existingData.bulle_10m || '',
                autre_fourniture: existingData.autre_fourniture || '',
                remarques: existingData.remarques || '',
                volume_override: existingData.volume_override != null && existingData.volume_override !== '' ? String(existingData.volume_override) : '',
                prix_prestation: htToTtc(existingData.prix_prestation),
                prestations: (existingData.prestations || []).map(p => ({
                    ...p,
                    prix: htToTtc(p?.prix),
                })),
            });
        } else if (prospect) {
            const addrDep = prospect.adresse_depart || {};
            const addrArr = prospect.adresse_arrivee || {};
            const getAddr = (a) => [a.adresse, a.code_postal, a.ville].filter(Boolean).join(', ');
            const sim = prospect.simulateur_devis || {};
            // Find a sibling formule with rich data to fall back on missing prospect-level fields
            const sibling = (otherFormules.find(f => !f.is_archived && f.data) || {}).data || {};
            const fb = (key, ...alts) => {
                for (const v of alts) {
                    if (v !== undefined && v !== null && v !== '') return v;
                }
                return sibling[key] ?? '';
            };
            setForm({
                nom: fb('nom', prospect.nom),
                telephone: fb('telephone', prospect.telephone),
                email: fb('email', prospect.email),
                // date_chargement may live either at top-level (set when user
                // picks a date via ProspectForm) or inside adresse_depart.date
                // (legacy / imported records). Read both before falling back.
                date_chargement: fb('date_chargement', prospect.date_chargement, addrDep.date),
                adresse_depart: fb('adresse_depart', getAddr(addrDep)),
                etage_depart: fb('etage_depart', addrDep.etage),
                portage_depart: fb('portage_depart', addrDep.portage),
                aot_depart: fb('aot_depart',
                    addrDep.autorisation_stationnement === true ? 'oui' : (addrDep.autorisation_stationnement === false ? 'non' : '')),
                monte_meuble_depart: fb('monte_meuble_depart',
                    addrDep.monte_meuble === true ? 'oui' : (addrDep.monte_meuble === false ? 'non' : '')),
                ascenseur_depart: fb('ascenseur_depart', addrDep.ascenseur),
                date_dechargement: fb('date_dechargement', prospect.date_dechargement, addrArr.date),
                adresse_arrivee: fb('adresse_arrivee', getAddr(addrArr)),
                etage_arrivee: fb('etage_arrivee', addrArr.etage),
                portage_arrivee: fb('portage_arrivee', addrArr.portage),
                aot_arrivee: fb('aot_arrivee',
                    addrArr.autorisation_stationnement === true ? 'oui' : (addrArr.autorisation_stationnement === false ? 'non' : '')),
                monte_meuble_arrivee: fb('monte_meuble_arrivee',
                    addrArr.monte_meuble === true ? 'oui' : (addrArr.monte_meuble === false ? 'non' : '')),
                ascenseur_arrivee: fb('ascenseur_arrivee', addrArr.ascenseur),
                garde_meuble: fb('garde_meuble', prospect.garde_meuble),
                garde_meuble_mensualite: fb('garde_meuble_mensualite', prospect.garde_meuble_mensualite),
                garde_meuble_duree: fb('garde_meuble_duree', prospect.garde_meuble_duree),
                fournitures: fb('fournitures', sim.fournitures_active ? 'oui' : ''),
                cartons_standards: fb('cartons_standards', sim.cartons_standards),
                cartons_livres: fb('cartons_livres', sim.cartons_livres),
                cartons_verres: fb('cartons_verres', sim.cartons_verres),
                cartons_bouteilles: fb('cartons_bouteilles', sim.cartons_bouteilles),
                adhesif: fb('adhesif', sim.adhesif),
                bulle_100m: fb('bulle_100m', sim.papier_bulle),
                bulle_10m: fb('bulle_10m', sim.bulle_10m),
                autre_fourniture: fb('autre_fourniture', sim.autres),
                // Remarques devis : toujours vide à la création — l'utilisateur
                // saisit ce qu'il veut. Ne pas copier ni la remarque prospect
                // ni celle d'un sibling (autre formule), pour ne pas imposer
                // un texte par défaut.
                remarques: '',
                // Volume affiche : laisser vide => le backend prendra le volume
                // calcule depuis l'inventaire. L'admin peut le forcer en tapant
                // un nombre (ex: 10 m3) pour un devis estimatif simplifie.
                volume_override: '',
                prix_prestation: '',
                prestations: [],
            });
        }
    }, [open, prospect, mode, existingData, otherFormules]);

    const update = (key, val) => { if (!isReadonly) setForm(p => ({ ...p, [key]: val })); };
    const inputCls = `bg-gray-50 border-gray-200 text-sm ${isReadonly ? 'cursor-default opacity-70' : ''}`;

    const addPrestation = (name) => {
        // Récupérer les prix disponibles dans le calcul des coûts du prospect.
        // Permet d'auto-remplir le prix de la prestation additionnelle même si
        // l'option correspondante n'est activée que d'un côté (depart OU arrivee)
        // ou si la valeur n'est saisie que dans cc (sans flag oui/non actif).
        // Note : cc.prix_* sont stockés en HT (Calcul des coûts). On les convertit
        // en TTC ici car la section 8 (Prix final) saisit en TTC.
        const cc = (prospect?.simulateur_devis?.calcul_couts) || {};
        const num = (v) => parseFloat(v) || 0;
        const toTtc = (htVal) => round2(htVal * TTC_FACTOR);

        // If the prestation corresponds to a synced option, turn the option on.
        // The useEffect below will auto-insert the prestation with its price.
        if (name === 'Monte meuble') {
            const prixHt = num(cc.prix_monte_meuble_depart?.value) + num(cc.prix_monte_meuble_arrivee?.value);
            const prix = toTtc(prixHt);
            setForm(p => {
                // Si une entrée auto existe déjà → ne rien faire
                if (p.prestations.some(x => x.auto && x.source === 'monte_meuble')) return p;
                // Activer le flag manquant pour cohérence visuelle
                const next = { ...p };
                if (next.monte_meuble_depart !== 'oui' && next.monte_meuble_arrivee !== 'oui') {
                    if (num(cc.prix_monte_meuble_arrivee?.value) > 0) next.monte_meuble_arrivee = 'oui';
                    else next.monte_meuble_depart = 'oui';
                }
                // Insérer directement la prestation avec le prix combiné
                next.prestations = [
                    ...next.prestations,
                    { name: 'Monte meuble', prix: prix > 0 ? String(prix) : '', auto: true, source: 'monte_meuble' },
                ];
                return next;
            });
        } else if (name === 'Autorisation stationnement') {
            const prixHt = num(cc.prix_aot_depart?.value) + num(cc.prix_aot_arrivee?.value);
            const prix = toTtc(prixHt);
            setForm(p => {
                if (p.prestations.some(x => x.auto && x.source === 'aot')) return p;
                const next = { ...p };
                if (next.aot_depart !== 'oui' && next.aot_arrivee !== 'oui') {
                    if (num(cc.prix_aot_arrivee?.value) > 0) next.aot_arrivee = 'oui';
                    else next.aot_depart = 'oui';
                }
                next.prestations = [
                    ...next.prestations,
                    { name: 'Autorisation stationnement', prix: prix > 0 ? String(prix) : '', auto: true, source: 'aot' },
                ];
                return next;
            });
        } else if (name === 'Garde meuble') {
            // garde_meuble_mensualite est saisi comme un prix mensuel "tout
            // public" — on l'affiche tel quel en TTC (pas de conversion).
            const prix = num(prospect?.garde_meuble_mensualite);
            setForm(p => {
                if (p.prestations.some(x => x.auto && x.source === 'garde_meuble')) return p;
                const next = { ...p, garde_meuble: 'oui' };
                next.prestations = [
                    ...next.prestations,
                    { name: 'Garde meuble', prix: prix > 0 ? String(prix) : (p.garde_meuble_mensualite || ''), auto: true, source: 'garde_meuble' },
                ];
                return next;
            });
        } else {
            setForm(p => ({ ...p, prestations: [...p.prestations, { name, prix: '' }] }));
        }
        setShowPrestationMenu(false);
    };

    const addCustomPrestation = () => {
        setForm(p => ({ ...p, prestations: [...p.prestations, { name: '', prix: '', custom: true }] }));
        setShowPrestationMenu(false);
    };

    const removePrestation = (idx) => {
        setForm(p => {
            const removed = p.prestations[idx];
            const next = { ...p, prestations: p.prestations.filter((_, i) => i !== idx) };
            // Mirror the removal back to the synced options
            if (removed?.auto) {
                if (removed.source === 'monte_meuble') {
                    next.monte_meuble_depart = 'non';
                    next.monte_meuble_arrivee = 'non';
                } else if (removed.source === 'aot') {
                    next.aot_depart = 'non';
                    next.aot_arrivee = 'non';
                } else if (removed.source === 'garde_meuble') {
                    next.garde_meuble = 'non';
                }
            }
            return next;
        });
    };

    const updatePrestation = (idx, key, val) => {
        setForm(p => {
            const updated = [...p.prestations];
            updated[idx] = { ...updated[idx], [key]: val };
            return { ...p, prestations: updated };
        });
    };

    // ──────────────────────────────────────────────────────────────
    // Two-way sync: options (Monte-meuble / AOT / Garde-meuble) ⇄
    // "Prestation additionnelle" list.
    //   • If an option is "oui" and no auto-prestation exists yet →
    //     insert one (reusing a matching manual entry if present).
    //   • If all related options flip to "non" → remove the auto entry.
    //   • Prices come from the prospect "Calcul des couts" section
    //     (prix_monte_meuble_*, prix_aot_*, garde_meuble_mensualite).
    //   • Manual entries stay intact (no `auto` flag).
    // ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || isReadonly) return;
        const cc = (prospect?.simulateur_devis?.calcul_couts) || {};
        const num = (v) => parseFloat(v) || 0;

        const mmActive = form.monte_meuble_depart === 'oui' || form.monte_meuble_arrivee === 'oui';
        const mmPrix =
            (form.monte_meuble_depart === 'oui' ? num(cc.prix_monte_meuble_depart?.value) : 0)
            + (form.monte_meuble_arrivee === 'oui' ? num(cc.prix_monte_meuble_arrivee?.value) : 0);

        const aotActive = form.aot_depart === 'oui' || form.aot_arrivee === 'oui';
        const aotPrix =
            (form.aot_depart === 'oui' ? num(cc.prix_aot_depart?.value) : 0)
            + (form.aot_arrivee === 'oui' ? num(cc.prix_aot_arrivee?.value) : 0);

        const gmActive = form.garde_meuble === 'oui';
        const gmPrix = gmActive ? num(form.garde_meuble_mensualite) : 0;

        setForm(p => {
            let next = p.prestations;
            let changed = false;

            const syncOne = (sourceKey, name, active, prix) => {
                const autoIdx = next.findIndex(x => x.auto === true && x.source === sourceKey);
                if (active) {
                    if (autoIdx === -1) {
                        // Claim an existing manual entry with the same name if any,
                        // otherwise insert a fresh auto entry.
                        const nameIdx = next.findIndex(x => !x.auto && x.name === name);
                        if (nameIdx !== -1) {
                            next = next.map((x, i) => i === nameIdx ? { ...x, auto: true, source: sourceKey } : x);
                        } else {
                            next = [...next, { name, prix: prix > 0 ? String(prix) : '', auto: true, source: sourceKey }];
                        }
                        changed = true;
                    }
                } else if (autoIdx !== -1) {
                    next = next.filter((_, i) => i !== autoIdx);
                    changed = true;
                }
            };

            syncOne('monte_meuble', 'Monte meuble', mmActive, mmPrix);
            syncOne('aot', 'Autorisation stationnement', aotActive, aotPrix);
            syncOne('garde_meuble', 'Garde meuble', gmActive, gmPrix);

            return changed ? { ...p, prestations: next } : p;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open, isReadonly,
        form.monte_meuble_depart, form.monte_meuble_arrivee,
        form.aot_depart, form.aot_arrivee,
        form.garde_meuble, form.garde_meuble_mensualite,
    ]);

    // Totals
    // The "8. Prix final" inputs are now entered in TTC by the user.
    // Backend stores prix_prestation / prestations[].prix as HT (unchanged
    // contract). We translate TTC ↔ HT at the UI boundary.
    // Totals — user inputs are in TTC, derive HT/TVA from them.
    const totalTTC = useMemo(() => {
        const main = parseFloat(form.prix_prestation) || 0;
        const extras = form.prestations.reduce((s, p) => s + (parseFloat(p.prix) || 0), 0);
        return main + extras;
    }, [form.prix_prestation, form.prestations]);
    const totalHT = round2(totalTTC / TTC_FACTOR);
    const tva = round2(totalTTC - totalHT);

    const [creating, setCreating] = useState(false);
    const resilient = useResilientSave();

    const handleSubmit = async () => {
        setCreating(true);
        try {
            // UI captures prices in TTC, but the backend contract is HT.
            // Convert TTC → HT just-in-time so server-side total_ht /
            // total_ttc remain consistent with what the user sees.
            const formForApi = {
                ...form,
                prix_prestation: ttcToHt(form.prix_prestation),
                prestations: form.prestations.map(p => ({
                    ...p,
                    prix: ttcToHt(p.prix),
                })),
            };
            const fn = isEdit && existingId
                ? () => axios.put(`${API_URL}/api/devis-formules/${existingId}`, { data: formForApi }, { withCredentials: true })
                : () => axios.post(`${API_URL}/api/devis-formules`, { devis_id: devisId, formule, data: formForApi }, { withCredentials: true });
            const r = await resilient.trySave(fn);
            if (!r.ok) {
                if (!r.offline) toast.error('Erreur');
                return;
            }
            toast.success(isEdit && existingId ? `Devis ${label} modifie` : `Devis ${label} cree`);
            onClose(true);
        } catch (err) {
            if (!isNetworkError(err)) toast.error('Erreur');
        } finally {
            setCreating(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="create-devis-modal">
                <DialogTitle className="sr-only">{title}</DialogTitle>
                {resilient.Modal}
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-[#1B365D]">{title}</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => onClose(false)} data-testid="modal-cancel">
                            {isReadonly ? 'Fermer' : 'Annuler'}
                        </Button>
                        {!isReadonly && (
                            <Button onClick={handleSubmit} disabled={creating} className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white" data-testid="modal-create">
                                {creating ? (isEdit ? 'Enregistrement...' : 'Creation...') : (isEdit ? 'Enregistrer modification' : 'Creer')}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="p-6 space-y-6 bg-gray-50/50">
                    {/* 1. Informations generales */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-infos">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                            <User className="w-4 h-4 text-[#1B365D]" />
                            <h3 className="text-sm font-bold text-[#1B365D]">1. Informations generales</h3>
                        </div>
                        <FieldRow label="Nom" required>
                            <Input value={form.nom} onChange={(e) => update('nom', e.target.value)} className="bg-gray-50 border-gray-200 text-sm" data-testid="modal-nom" />
                        </FieldRow>
                        <FieldRow label="Telephone">
                            <Input value={form.telephone} onChange={(e) => update('telephone', e.target.value)} className="bg-gray-50 border-gray-200 text-sm" data-testid="modal-tel" />
                        </FieldRow>
                        <FieldRow label="Email">
                            <Input value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="email@exemple.fr" className="bg-gray-50 border-gray-200 text-sm" data-testid="modal-email" />
                        </FieldRow>
                    </div>

                    {/* 2. Adresse de depart */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-depart">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                            <MapPin className="w-4 h-4 text-green-500" />
                            <h3 className="text-sm font-bold text-[#1B365D]">2. Adresse de depart</h3>
                        </div>
                        <FieldRow label="Date de chargement" required>
                            <Input type="date" value={form.date_chargement} onChange={(e) => update('date_chargement', e.target.value)} className="bg-gray-50 border-gray-200 text-sm" data-testid="modal-date-chargement" />
                        </FieldRow>
                        <FieldRow label="Adresse">
                            <AddressAutocomplete
                                value={form.adresse_depart}
                                onChange={(val) => update('adresse_depart', val)}
                                placeholder="Saisir l'adresse complete"
                                inputClassName="text-sm"
                                testId="devis-adresse-depart"
                            />
                        </FieldRow>
                        <FieldRow label="Etage">
                            <Input value={form.etage_depart} onChange={(e) => update('etage_depart', e.target.value)} placeholder="Ex: 3eme" className="bg-gray-50 border-gray-200 text-sm" />
                        </FieldRow>
                        <FieldRow label="Ascenseur">
                            <select value={form.ascenseur_depart} onChange={(e) => update('ascenseur_depart', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                        <FieldRow label="Portage">
                            <Input value={form.portage_depart} onChange={(e) => update('portage_depart', e.target.value)} placeholder="Ex: 20m" className="bg-gray-50 border-gray-200 text-sm" />
                        </FieldRow>
                        <FieldRow label="Autorisation stationnement">
                            <select value={form.aot_depart} onChange={(e) => update('aot_depart', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                        <FieldRow label="Monte meuble">
                            <select value={form.monte_meuble_depart} onChange={(e) => update('monte_meuble_depart', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                    </div>

                    {/* 3. Adresse d'arrivee */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-arrivee">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                            <MapPin className="w-4 h-4 text-red-500" />
                            <h3 className="text-sm font-bold text-[#1B365D]">3. Adresse d'arrivee</h3>
                        </div>
                        <FieldRow label="Date de dechargement">
                            <Input type="date" value={form.date_dechargement} onChange={(e) => update('date_dechargement', e.target.value)} className="bg-gray-50 border-gray-200 text-sm" data-testid="modal-date-dechargement" />
                        </FieldRow>
                        <FieldRow label="Adresse">
                            <AddressAutocomplete
                                value={form.adresse_arrivee}
                                onChange={(val) => update('adresse_arrivee', val)}
                                placeholder="Saisir l'adresse complete"
                                inputClassName="text-sm"
                                testId="devis-adresse-arrivee"
                            />
                        </FieldRow>
                        <FieldRow label="Etage">
                            <Input value={form.etage_arrivee} onChange={(e) => update('etage_arrivee', e.target.value)} placeholder="Ex: 3eme" className="bg-gray-50 border-gray-200 text-sm" />
                        </FieldRow>
                        <FieldRow label="Ascenseur">
                            <select value={form.ascenseur_arrivee} onChange={(e) => update('ascenseur_arrivee', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                        <FieldRow label="Portage">
                            <Input value={form.portage_arrivee} onChange={(e) => update('portage_arrivee', e.target.value)} placeholder="Ex: 20m" className="bg-gray-50 border-gray-200 text-sm" />
                        </FieldRow>
                        <FieldRow label="Autorisation stationnement">
                            <select value={form.aot_arrivee} onChange={(e) => update('aot_arrivee', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                        <FieldRow label="Monte meuble">
                            <select value={form.monte_meuble_arrivee} onChange={(e) => update('monte_meuble_arrivee', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                    </div>

                    {/* 4. Garde meuble */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-garde-meuble">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                            <Package className="w-4 h-4 text-[#1B365D]" />
                            <h3 className="text-sm font-bold text-[#1B365D]">4. Garde-meuble</h3>
                        </div>
                        <FieldRow label="Garde-meuble" required>
                            <select value={form.garde_meuble} onChange={(e) => update('garde_meuble', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                        {form.garde_meuble === 'oui' && (
                            <>
                                <FieldRow label="Mensualite (tarif)">
                                    <Input value={form.garde_meuble_mensualite} onChange={(e) => update('garde_meuble_mensualite', e.target.value)}
                                        placeholder="Ex: 150&#8364;/mois" className="bg-gray-50 border-gray-200 text-sm" />
                                </FieldRow>
                                <FieldRow label="Duree">
                                    <Input value={form.garde_meuble_duree} onChange={(e) => update('garde_meuble_duree', e.target.value)}
                                        placeholder="Ex: 3 mois" className="bg-gray-50 border-gray-200 text-sm" />
                                </FieldRow>
                            </>
                        )}
                    </div>

                    {/* 5. Fournitures */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-fournitures">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                            <Gift className="w-4 h-4 text-[#1B365D]" />
                            <h3 className="text-sm font-bold text-[#1B365D]">5. Fournitures</h3>
                        </div>
                        <FieldRow label="Fournitures" required>
                            <select value={form.fournitures} onChange={(e) => update('fournitures', e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20">
                                <option value="">Selectionner</option>
                                <option value="oui">Oui</option>
                                <option value="non">Non</option>
                            </select>
                        </FieldRow>
                        {form.fournitures === 'oui' && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                                {[
                                    { key: 'cartons_standards', label: 'Cartons standards' },
                                    { key: 'cartons_livres', label: 'Cartons livres' },
                                    { key: 'cartons_verres', label: 'Cartons verres (100 verres)' },
                                    { key: 'cartons_bouteilles', label: 'Cartons bouteilles' },
                                    { key: 'adhesif', label: 'Adhesif' },
                                    { key: 'bulle_100m', label: 'Bulle (100m x 50cm)' },
                                    { key: 'bulle_10m', label: 'Bulle (10m x 1m)' },
                                    { key: 'autre_fourniture', label: 'Autre fourniture' },
                                ].map(({ key, label }) => (
                                    <div key={key}>
                                        <label className="text-xs text-red-400 font-medium mb-1 block">{label}</label>
                                        <Input value={form[key]} onChange={(e) => update(key, e.target.value)}
                                            placeholder="" className="bg-gray-50 border-gray-200 text-sm" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 6. Volume */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-volume">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                            <Layers className="w-4 h-4 text-[#1B365D]" />
                            <h3 className="text-sm font-bold text-[#1B365D]">6. Volume</h3>
                        </div>
                        {(() => {
                            const items = volumeItems || [];
                            const totalVol = items.reduce((s, i) => s + ((parseFloat(i.volume) || 0) * (parseInt(i.quantity) || 0)), 0);
                            const totalCount = items.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0);
                            return (
                                <>
                                    <div className="bg-[#1B365D] text-white rounded-xl p-5">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Volume total</p>
                                                <p className="text-sm text-white/70 mt-0.5">{totalCount} article{totalCount !== 1 ? 's' : ''}</p>
                                            </div>
                                            <p className="text-3xl font-bold flex items-baseline gap-1">
                                                {totalVol.toFixed(2)}
                                                <span className="text-lg font-normal text-[#C9A962]">m³</span>
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowVolumeDetails(!showVolumeDetails)}
                                        className="w-full text-center py-3 text-sm font-medium text-[#1B365D] hover:text-[#C9A962] transition-colors flex items-center justify-center gap-1.5"
                                    >
                                        {showVolumeDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        {showVolumeDetails ? 'Masquer les details' : 'Voir les details'}
                                    </button>
                                    {showVolumeDetails && items.length > 0 && (
                                        <div className="space-y-1 max-h-[200px] overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                                            {items.map((item, idx) => {
                                                const t = (parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0);
                                                return (
                                                    <div key={idx} className="flex items-center justify-between px-4 py-2 text-sm">
                                                        <span className="text-[#0F172A]">{item.name} <span className="text-gray-400 text-xs">x{item.quantity}</span></span>
                                                        <span className="text-gray-500 text-xs shrink-0">{t.toFixed(2)} m³</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>

                    {/* Remarque prospect (lecture seule, suit le prospect sur toutes les formules) */}
                    {(prospect?.remarques || '').trim() && (
                        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-remarque-prospect">
                            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-emerald-600" />
                                    <h3 className="text-sm font-bold text-[#1B365D]">Remarque prospect</h3>
                                    <span className="ml-1 inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        Synchronisée avec le prospect
                                    </span>
                                </div>
                            </div>
                            <div className="pl-3 border-l-2 border-emerald-400">
                                <div
                                    className="bg-emerald-50/40 border border-emerald-100 rounded-md px-3 py-2.5 text-sm text-[#0F172A] whitespace-pre-wrap break-words"
                                    data-testid="modal-remarque-prospect-content"
                                >
                                    {prospect.remarques}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 7. Affichage devis — volume + remarques affiches sur le PDF */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-affichage-devis">
                        <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-[#1B365D]" />
                                <h3 className="text-sm font-bold text-[#1B365D]">7. Affichage devis</h3>
                                <span className="text-[10px] text-gray-400 font-normal">Ce qui apparaitra sur le PDF</span>
                            </div>
                        </div>

                        {/* Manual volume override */}
                        <div className="mb-4">
                            <label className="block text-xs font-medium text-[#1B365D] mb-1.5">
                                Volume a afficher (m<sup>3</sup>)
                            </label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.volume_override || ''}
                                    onChange={(e) => update('volume_override', e.target.value)}
                                    placeholder="Auto (inventaire)"
                                    className="w-32 bg-gray-50 border-gray-200 text-sm text-right"
                                    data-testid="modal-volume-override"
                                />
                                <span className="text-xs text-gray-400">
                                    Laisser vide pour utiliser le volume de l&apos;inventaire automatiquement.
                                </span>
                            </div>
                        </div>

                        {/* Remarques devis */}
                        <div>
                            <label className="block text-xs font-medium text-[#1B365D] mb-1.5">
                                Remarques devis ({label})
                            </label>
                            <div className="pl-3 border-l-2 border-red-400">
                                <Textarea
                                    value={form.remarques}
                                    onChange={(e) => update('remarques', e.target.value)}
                                    placeholder="Notes et remarques affichees sur le devis PDF..."
                                    className="bg-gray-50 border-gray-200 text-sm min-h-[100px] resize-y"
                                    data-testid="modal-remarques"
                                />
                            </div>
                            <p className="mt-1 text-[10px] text-gray-400">
                                La remarque prospect (interne) n&apos;apparait pas sur le PDF — seule cette zone est imprimee.
                            </p>
                        </div>
                    </div>

                    {/* 7b. Calcul des couts (synchronise avec le prospect) */}
                    {(() => {
                        const sim = prospect?.simulateur_devis || {};
                        const cc = sim.calcul_couts || {};
                        const num = (v) => {
                            const n = parseFloat(v);
                            return isNaN(n) ? 0 : n;
                        };
                        const nbJours = parseInt(cc.nombre_jours?.[formule]) || 0;
                        const tarif = num(cc.tarif_demenageur?.[formule]);
                        const panier = num(cc.panier?.[formule]);
                        const hotel = num(cc.hotel?.[formule]);
                        const marge = num(cc.marge?.[formule]);
                        // ===== STRICTEMENT IDENTIQUE A ProspectForm.computeTotals =====
                        // Fournitures
                        const fournAct = sim.fournitures_active === true;
                        const f = settingsTarifs.fournitures || {};
                        const priceCartonStd = num(f.carton_standard);
                        const priceCartonLivre = num(f.carton_livre);
                        const priceAdhesif = num(f.adhesif);
                        const priceBulle100 = num(f.bulle_100m);
                        const fournituresCost = fournAct
                            ? num(sim.cartons_standards) * priceCartonStd
                              + num(sim.cartons_livres) * priceCartonLivre
                              + num(sim.adhesif) * priceAdhesif
                              + num(sim.papier_bulle) * priceBulle100
                              + num(sim.autres) * 1
                            : 0;
                        // Distance: D × (camion_20m3 + camion_30m3) × tarif_km
                        const tarifKm = num(settingsTarifs.tarif_km);
                        const nbCamions = num(sim.camion_20m3) + num(sim.camion_30m3);
                        const distanceCost = num(sim.distance) * nbCamions * tarifKm;
                        // AOT / monte-meuble (uniquement si activé sur l'adresse correspondante)
                        const _isYes = (v) => v === 'oui' || v === true;
                        const aotDep = _isYes(prospect?.adresse_depart?.autorisation_stationnement) ? num(cc.prix_aot_depart?.value) : 0;
                        const aotArr = _isYes(prospect?.adresse_arrivee?.autorisation_stationnement) ? num(cc.prix_aot_arrivee?.value) : 0;
                        const mmDep = _isYes(prospect?.adresse_depart?.monte_meuble) ? num(cc.prix_monte_meuble_depart?.value) : 0;
                        const mmArr = _isYes(prospect?.adresse_arrivee?.monte_meuble) ? num(cc.prix_monte_meuble_arrivee?.value) : 0;
                        const sharedCost = aotDep + aotArr + mmDep + mmArr;
                        // Per-formule : tarif + panier + hotel + marge
                        const perCost = tarif + panier + hotel + marge;
                        // Total final
                        const totalHT = fournituresCost + distanceCost + perCost + sharedCost;
                        const totalTTCCouts = totalHT * 1.2;
                        const hasData = nbJours > 0 || tarif > 0 || panier > 0 || hotel > 0 || marge > 0;
                        const rows = [
                            { label: 'Nombre de jours', value: nbJours || '', unit: '' },
                            ...Array.from({ length: nbJours }, (_, i) => ({
                                label: `Effectifs jr ${i + 1}`,
                                value: cc[`effectifs_jr_${i + 1}`]?.[formule] || '',
                                unit: '',
                            })),
                            { label: 'Tarif demenageur', value: tarif || '', unit: '€' },
                            { label: 'Panier', value: panier || '', unit: '€' },
                            { label: 'Hotel', value: hotel || '', unit: '€' },
                            ...(distanceCost > 0 ? [{ label: `Distance (${num(sim.distance)} km × ${nbCamions} camion${nbCamions > 1 ? 's' : ''})`, value: distanceCost, unit: '€', accent: true }] : []),
                            ...(fournituresCost > 0 ? [{ label: 'Fournitures', value: fournituresCost, unit: '€', accent: true }] : []),
                            ...(aotDep > 0 ? [{ label: 'Prix AOT depart', value: aotDep, unit: '€', accent: true }] : []),
                            ...(aotArr > 0 ? [{ label: 'Prix AOT arrivee', value: aotArr, unit: '€', accent: true }] : []),
                            ...(mmDep > 0 ? [{ label: 'Prix Monte-meuble depart', value: mmDep, unit: '€', accent: true }] : []),
                            ...(mmArr > 0 ? [{ label: 'Prix Monte-meuble arrivee', value: mmArr, unit: '€', accent: true }] : []),
                            { label: 'Marge', value: marge || '', unit: '€' },
                        ];
                        return (
                            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-calcul-couts">
                                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">7b. Calcul des couts ({label})</h3>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Synchronise avec le prospect</span>
                                    </div>
                                    {totalHT > 0 && <span className="w-4 h-4 rounded-full bg-green-400 flex items-center justify-center text-white text-[10px]">&#10003;</span>}
                                </div>
                                {!hasData ? (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800" data-testid="calcul-couts-empty-modal">
                                        Aucune donnee de calcul des couts pour cette formule. Completez la section <strong>Calcul des couts</strong> dans le prospect pour voir les valeurs ici.
                                    </div>
                                ) : (
                                    <div className="pl-3 border-l-2 border-[#C9A962] space-y-1.5" data-testid="calcul-couts-rows">
                                        {rows.map(({ label: rLabel, value, unit, accent }, idx) => (
                                            <div key={idx} className={`flex items-center justify-between py-1.5 px-2 rounded ${accent ? 'bg-blue-50/40' : ''}`}>
                                                <span className={`text-sm ${accent ? 'text-blue-700 italic' : 'text-gray-700'}`}>{rLabel}</span>
                                                <span className={`text-sm font-semibold ${accent ? 'text-blue-700' : 'text-[#1B365D]'}`}>
                                                    {value !== '' && value !== 0 ? (unit ? `${Number(value).toFixed(2).replace('.', ',')} ${unit}` : value) : '—'}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-200">
                                            <span className="text-sm font-bold text-[#1B365D]">Total HT</span>
                                            <span className="text-sm font-bold text-[#1B365D]">{totalHT.toFixed(2).replace('.', ',')} €</span>
                                        </div>
                                        <div className="flex items-center justify-between py-1.5 bg-[#1B365D]/5 px-2 rounded">
                                            <span className="text-sm font-bold text-blue-600">Total TTC</span>
                                            <span className="text-sm font-bold text-blue-600">{totalTTCCouts.toFixed(2).replace('.', ',')} €</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* 8. Prix final */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modal-section-prix">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                            <DollarSign className="w-4 h-4 text-[#1B365D]" />
                            <h3 className="text-sm font-bold text-[#1B365D]">8. Prix final</h3>
                            {totalTTC > 0 && <span className="w-4 h-4 rounded-full bg-green-400 flex items-center justify-center text-white text-[10px]">&#10003;</span>}
                        </div>

                        {/* Main prestation */}
                        <div className="pl-3 border-l-2 border-green-400 space-y-3">
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-[#1B365D] flex-1">Prestation demenagement - {label}</span>
                                <Input
                                    type="number" min="0" step="0.01"
                                    value={form.prix_prestation}
                                    onChange={(e) => update('prix_prestation', e.target.value)}
                                    placeholder="0.00"
                                    className="w-24 bg-gray-50 border-gray-200 text-sm text-right"
                                    data-testid="modal-prix-main"
                                />
                                <span className="text-xs text-gray-400 shrink-0">&#8364; TTC</span>
                            </div>

                            {/* Additional prestations */}
                            {form.prestations.map((p, idx) => (
                                <div key={idx} className="flex items-center gap-3 ml-4 border-l-2 border-gray-200 pl-3">
                                    {p.custom ? (
                                        <Input
                                            value={p.name}
                                            onChange={(e) => updatePrestation(idx, 'name', e.target.value)}
                                            placeholder="Nom de la prestation"
                                            className="flex-1 bg-gray-50 border-gray-200 text-sm"
                                        />
                                    ) : (
                                        <span className="flex-1 text-sm text-gray-700">{p.name}</span>
                                    )}
                                    <Input
                                        type="number" min="0" step="0.01"
                                        value={p.prix}
                                        onChange={(e) => updatePrestation(idx, 'prix', e.target.value)}
                                        placeholder="0.00"
                                        className="w-24 bg-gray-50 border-gray-200 text-sm text-right"
                                        data-testid={`modal-prix-extra-${idx}`}
                                    />
                                    <span className="text-xs text-gray-400 shrink-0">&#8364; TTC</span>
                                    <button onClick={() => removePrestation(idx)} className="text-gray-300 hover:text-red-400 transition-colors" data-testid={`modal-remove-extra-${idx}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}

                            {/* Add prestation button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPrestationMenu(!showPrestationMenu)}
                                    className="w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:text-[#1B365D] hover:border-[#1B365D]/30 transition-colors flex items-center justify-center gap-1.5"
                                    data-testid="modal-add-prestation"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Prestation additionnelle
                                </button>
                                {showPrestationMenu && (
                                    <div className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 w-64" data-testid="prestation-menu">
                                        {PRESTATION_OPTIONS.map((opt) => (
                                            <button key={opt} onClick={() => addPrestation(opt)}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                                {opt}
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-100 mt-1 pt-1">
                                            <button onClick={addCustomPrestation}
                                                className="w-full text-left px-4 py-2.5 text-sm text-blue-600 font-medium hover:bg-blue-50 transition-colors">
                                                + Personnalise
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Totals */}
                        <div className="mt-5 pt-4 border-t border-gray-200 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Total HT</span>
                                <span className="text-sm font-medium text-gray-900">{totalHT.toFixed(2)} &#8364;</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">TVA (20%)</span>
                                <span className="text-sm font-medium text-gray-900">{tva.toFixed(2)} &#8364;</span>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                <span className="text-base font-bold text-[#1B365D]">Total TTC</span>
                                <span className="text-base font-bold text-[#1B365D]">{totalTTC.toFixed(2)} &#8364;</span>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ========== MAIN DEVIS DETAIL PAGE ========== */
export default function DevisDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canWrite: canWriteDevis, canDelete: canDeleteDevis, canChangeStatus: canChangeStatutDevis } = useCan('devis');
    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFormule, setSelectedFormule] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [modalMode, setModalMode] = useState('create');
    const [modalExistingData, setModalExistingData] = useState(null);
    const [modalExistingId, setModalExistingId] = useState(null);
    const [sendModalOpen, setSendModalOpen] = useState(false);
    const [sendSelected, setSendSelected] = useState([]);
    const [sendCivilite, setSendCivilite] = useState('M.');
    const [sendEmail, setSendEmail] = useState('');
    const [sendSubject, setSendSubject] = useState('');
    const [sendMessage, setSendMessage] = useState('');
    const [sendTemplate, setSendTemplate] = useState('visite'); // 'estimatif' | 'listing' | 'visite'
    const [sending, setSending] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    // Settings-driven attached files (selectable on send)
    const [settingsFiles, setSettingsFiles] = useState([]);
    const [settingsTarifs, setSettingsTarifs] = useState({ fournitures: {}, tarif_km: 0 });
    const [sendAttachFileIds, setSendAttachFileIds] = useState([]);
    const [statutDropOpen, setStatutDropOpen] = useState(false);
    const [acceptModalOpen, setAcceptModalOpen] = useState(false);
    const [acceptSelectedDf, setAcceptSelectedDf] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [refuseModalOpen, setRefuseModalOpen] = useState(false);
    const [refuseReason, setRefuseReason] = useState('');
    const [refusing, setRefusing] = useState(false);
    const [relanceModalOpen, setRelanceModalOpen] = useState(false);
    const [pendingRelanceStatus, setPendingRelanceStatus] = useState('');
    const [relanceRemark, setRelanceRemark] = useState('');
    const [relanceNextDate, setRelanceNextDate] = useState('');
    const [relanceJustifFile, setRelanceJustifFile] = useState(null); // optional File
    const [relanceJustifUploading, setRelanceJustifUploading] = useState(false);
    const [savingRelance, setSavingRelance] = useState(false);
    const [sendingRelanceEmail, setSendingRelanceEmail] = useState(false);
    const [relanceEmailDialogOpen, setRelanceEmailDialogOpen] = useState(false);
    const [relanceCivilite, setRelanceCivilite] = useState('Mme');
    const [relanceSuccessOpen, setRelanceSuccessOpen] = useState(false);

    const fetchDevis = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/devis/${id}`, { withCredentials: true });
            setDevis(res.data);
        } catch {
            toast.error('Erreur lors du chargement du devis');
            navigate('/devis');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDevis(); }, [id]);

    // Load settings files (shared attachments available at send time)
    useEffect(() => {
        axios.get(`${API_URL}/api/settings`, { withCredentials: true })
            .then(res => {
                setSettingsFiles(res.data?.files || []);
                // Same source of truth as ProspectForm.js : compute HT/TTC
                // identically on both pages. Prices are nested under `tarifs`.
                const t = res.data?.tarifs || {};
                setSettingsTarifs({
                    fournitures: t.fournitures || {},
                    tarif_km: parseFloat(t.tarif_km) || 0,
                });
            })
            .catch(() => {});
    }, []);

    if (loading) return (
        <Layout>
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        </Layout>
    );

    if (!devis) return null;

    const prospect = devis.prospect || {};
    const formules = prospect.formules || [];
    const sCfg = STATUT_CONFIG[devis.statut_devis] || STATUT_CONFIG.a_envoyer;
    const adresseDepart = prospect.adresse_depart;
    const adresseArrivee = prospect.adresse_arrivee;
    const eventType = devis.inventaire?.event_type || '';

    const getAdresseText = (addr) => {
        if (!addr || !addr.adresse) return null;
        return [addr.adresse, addr.code_postal, addr.ville].filter(Boolean).join(', ') || null;
    };

    const openFormuleModal = (f) => {
        setSelectedFormule(f);
        setModalMode('create');
        setModalExistingData(null);
        setModalExistingId(null);
        setModalOpen(true);
    };

    const openViewModal = (df) => {
        setSelectedFormule(df.formule);
        setModalMode('view');
        setModalExistingData(df.data || {});
        setModalExistingId(df.id);
        setModalOpen(true);
        setOpenMenuId(null);
    };

    const openEditModal = (df) => {
        setSelectedFormule(df.formule);
        setModalMode('edit');
        setModalExistingData(df.data || {});
        setModalExistingId(df.id);
        setModalOpen(true);
        setOpenMenuId(null);
    };

    // ============ EMAIL TEMPLATES PAR TYPE DE DEVIS ============
    const MAIL_TEMPLATES = {
        estimatif: {
            label: 'Estimatif',
            color: 'bg-amber-100 text-amber-800 border-amber-200',
            subject: () => `Envoi de votre devis estimatif`,
            body: ({ civilite, nom }) => (
`Bonjour ${civilite} ${nom},

Suite a votre demande, nous vous prions de bien vouloir trouver ci-joint notre devis estimatif pour votre demenagement.

Ce devis est base sur les informations communiquees et pourra etre ajuste si necessaire.

Vous trouverez egalement joint a notre devis l'Attestation d'assurance, document obligatoire pour tous demenageurs en conformite avec la legislation.

Nous restons a votre disposition pour toute precision ou ajustement.`
            ),
        },
        listing: {
            label: 'Listing',
            color: 'bg-blue-100 text-blue-800 border-blue-200',
            subject: () => `Envoi de votre devis detaille`,
            body: ({ civilite, nom }) => (
`Bonjour ${civilite} ${nom},

Suite a l'etablissement de votre listing, nous vous prions de bien vouloir trouver ci-joint notre devis detaille correspondant a votre demenagement.

Ce devis prend en compte les elements recenses et vous offre une estimation plus precise.

Vous trouverez egalement joint a notre devis l'Attestation d'assurance, document obligatoire pour tous demenageurs en conformite avec la legislation.

Nous esperons que cette proposition repondra a vos attentes.`
            ),
        },
        visite: {
            label: 'Visite',
            color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
            subject: () => `Suite a votre rendez-vous - Envoi de vos devis`,
            body: ({ civilite, nom }) => (
`Bonjour ${civilite} ${nom},

Suite a votre rendez-vous avec notre commercial, nous vous prions de bien vouloir trouver ci-joint nos devis correspondants.

Vous trouverez egalement joint a notre devis l'Attestation d'assurance, document obligatoire pour tous demenageurs en conformite avec la legislation.

Nous esperons que nos devis vous conviennent et que vous opterez pour LUXY MOVE DEMENAGEMENT pour votre demenagement.

Pour toutes questions ou demande supplementaire, n'hesitez pas a nous contacter. Nous sommes a votre entiere disposition pour vous accompagner dans votre projet.

En vous souhaitant bonne reception, nous vous prions de bien vouloir agreer nos sinceres salutations.`
            ),
        },
    };

    // Auto-detect template from the prospect statut (first open)
    const detectTemplate = () => {
        const statut = devis?.prospect?.statut || '';
        if (statut === 'visite_terminee' || statut === 'visite_presentiel' || statut === 'visite_virtuelle') return 'visite';
        if (statut === 'listing_telephone' || statut === 'listing_recu' || statut === 'listing_virtuel') return 'listing';
        if (statut === 'devis_estimatif' || statut === 'nouveau' || statut === 'appel_a_rappeler') return 'estimatif';
        return 'visite';
    };

    const applyTemplate = (templateKey, civilite, nom) => {
        const tpl = MAIL_TEMPLATES[templateKey] || MAIL_TEMPLATES.visite;
        setSendSubject(tpl.subject({ civilite, nom }));
        setSendMessage(tpl.body({ civilite, nom }));
    };

    const openSendModal = () => {
        const active = (devis?.devis_formules || []).filter(df => !df.is_archived);
        setSendSelected(active.map(df => df.id));
        const clientEmail = devis?.prospect?.email || active[0]?.data?.email || '';
        setSendEmail(clientEmail);
        const clientNom = devis?.nom || '';
        setSendCivilite('M.');
        const tpl = detectTemplate();
        setSendTemplate(tpl);
        applyTemplate(tpl, 'M.', clientNom);
        setSendModalOpen(true);
    };

    const handleTemplateChange = (newTpl) => {
        setSendTemplate(newTpl);
        applyTemplate(newTpl, sendCivilite, devis?.nom || '');
    };

    const handleSendMultiple = async () => {
        if (!sendSelected.length || !sendEmail) return;
        // Validate email format and TLD locally before hitting the server (avoids waiting for an SMTP rejection)
        const emailTrimmed = sendEmail.trim();
        // Basic RFC: local@domain with at least one dot in domain and a 2+ letter TLD
        const emailRe = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
        if (!emailRe.test(emailTrimmed)) {
            toast.error(
                "Email invalide : le domaine doit exister (ex: gmail.com, orange.fr). L'adresse renseignée n'a pas l'air valide.",
                { duration: 8000 }
            );
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(`${API_URL}/api/devis-formules/send-multiple`, {
                devis_formule_ids: sendSelected,
                email: emailTrimmed,
                subject: sendSubject,
                message: sendMessage,
                civilite: sendCivilite,
                attach_file_ids: sendAttachFileIds,
            }, { withCredentials: true });
            setSendModalOpen(false);
            setSuccessDialogOpen(true);
            fetchDevis();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi', { duration: 8000 });
        } finally {
            setSending(false);
        }
    };

    const toggleSendSelection = (dfId) => {
        setSendSelected(prev => prev.includes(dfId) ? prev.filter(id => id !== dfId) : [...prev, dfId]);
    };

    // Active devis formules for send menu
    const activeDevisFormules = (devis?.devis_formules || []).filter(df => !df.is_archived);

    // Compute available statuts (no time/ordering restrictions anymore)
    const getAvailableStatuts = () => {
        const available = {};
        Object.entries(STATUT_CONFIG).forEach(([key, cfg]) => {
            if (key === 'a_envoyer') return;
            available[key] = cfg;
        });
        return available;
    };

    const availableStatuts = getAvailableStatuts();

    const handleStatutChange = async (newStatut) => {
        setStatutDropOpen(false);
        if (newStatut === 'accepte') {
            setAcceptSelectedDf(null);
            setAcceptModalOpen(true);
            return;
        }
        if (newStatut === 'refuse') {
            setRefuseReason('');
            setRefuseModalOpen(true);
            return;
        }
        if (newStatut === 'relance_1' || newStatut === 'relance_2' || newStatut === 'relance_3' || newStatut === 'relance_whatsapp') {
            setPendingRelanceStatus(newStatut);
            setRelanceRemark('');
            setRelanceNextDate('');
            setRelanceJustifFile(null);
            setRelanceModalOpen(true);
            return;
        }
        try {
            await axios.put(`${API_URL}/api/devis/${id}`, { statut_devis: newStatut }, { withCredentials: true });
            toast.success('Statut mis a jour');
            fetchDevis();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du changement de statut');
        }
    };

    const handleRelance1Confirm = async () => {
        if (!relanceRemark.trim()) { toast.error('Remarque requise'); return; }
        // WhatsApp = last step → date next not required
        const isWhatsapp = pendingRelanceStatus === 'relance_whatsapp';
        if (!isWhatsapp && !relanceNextDate) { toast.error('Veuillez choisir une date'); return; }
        setSavingRelance(true);
        try {
            // Optional justificatif upload (photo / PDF)
            let justifPayload = null;
            if (relanceJustifFile) {
                setRelanceJustifUploading(true);
                try {
                    const fd = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(relanceJustifFile);
                    });
                    const up = await axios.post(
                        `${API_URL}/api/devis/${id}/justificatif-relance`,
                        { file_data: fd, file_name: relanceJustifFile.name },
                        { withCredentials: true },
                    );
                    justifPayload = { url: up.data?.url, file_name: up.data?.file_name };
                } catch {
                    toast.error("Echec de l'upload du justificatif");
                    setRelanceJustifUploading(false);
                    setSavingRelance(false);
                    return;
                } finally {
                    setRelanceJustifUploading(false);
                }
            }

            const payload = {
                statut_devis: pendingRelanceStatus,
                relance_remark: relanceRemark.trim(),
            };
            if (relanceNextDate) payload.next_relance_date = relanceNextDate;
            if (justifPayload) payload.relance_justificatif = justifPayload;
            await axios.put(`${API_URL}/api/devis/${id}`, payload, { withCredentials: true });
            const labels = { relance_1: 'Relance 1', relance_2: 'Relance 2', relance_3: 'Relance 3', relance_whatsapp: 'Relance WhatsApp' };
            toast.success(`${labels[pendingRelanceStatus] || 'Relance'} enregistrée`);
            setRelanceModalOpen(false);
            fetchDevis();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSavingRelance(false);
        }
    };

    const handleRefuse = async () => {
        if (!refuseReason.trim()) return;
        setRefusing(true);
        try {
            await axios.put(`${API_URL}/api/devis/${id}`, {
                statut_devis: 'refuse',
                refuse_reason: refuseReason,
            }, { withCredentials: true });
            toast.success('Devis marque comme refuse');
            setRefuseModalOpen(false);
            fetchDevis();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setRefusing(false);
        }
    };

    const handleAcceptDevis = async () => {
        if (!acceptSelectedDf) return;
        setAccepting(true);
        try {
            await axios.post(`${API_URL}/api/devis/${id}/accept`, {
                devis_formule_id: acceptSelectedDf
            }, { withCredentials: true });
            toast.success('Devis accepte - Client cree');
            setAcceptModalOpen(false);
            fetchDevis();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setAccepting(false);
        }
    };

    const isEnvoye = devis.statut_devis && devis.statut_devis !== 'a_envoyer';

    // True if relance_1, relance_2 AND relance_3 have been performed at least once
    const has3Relances = (() => {
        const hist = Array.isArray(devis.relance_history) ? devis.relance_history : [];
        const done = new Set(hist.map((e) => e.statut));
        return done.has('relance_1') && done.has('relance_2') && done.has('relance_3');
    })();

    const handleSendRelanceEmail = () => {
        if (!has3Relances) return;
        setRelanceCivilite('Mme');
        setRelanceEmailDialogOpen(true);
    };

    const confirmSendRelanceEmail = async () => {
        setSendingRelanceEmail(true);
        try {
            await axios.post(
                `${API_URL}/api/devis/${id}/send-relance-email`,
                { civilite: relanceCivilite },
                { withCredentials: true }
            );
            setRelanceEmailDialogOpen(false);
            setRelanceSuccessOpen(true);
            fetchDevis();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi de la relance');
        } finally {
            setSendingRelanceEmail(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6" data-testid="devis-detail-page">
                {/* Back button + Envoyer - sticky */}
                <div className="flex items-center justify-between sticky top-0 z-30 bg-[#F3F4F6] py-3 -mt-4 lg:-mt-8 -mx-4 lg:-mx-8 px-4 lg:px-8 border-b border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/devis')}
                            className="text-[#1B365D] hover:bg-[#1B365D]/10" data-testid="back-button">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <h1 className="text-xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Devis — {devis.nom}
                        </h1>
                        <ImportantNotesButton
                            prospectId={devis.prospect_id}
                            prospectName={devis.nom}
                            variant="badge"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        {has3Relances && (
                            <Button
                                className="bg-orange-600 hover:bg-orange-700 text-white gap-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="send-relance-email-btn"
                                disabled={sendingRelanceEmail || !canWriteDevis}
                                title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : 'Envoyer un email de relance au client'}
                                onClick={handleSendRelanceEmail}
                            >
                                {sendingRelanceEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                {sendingRelanceEmail ? 'Envoi...' : 'Relancer par mail'}
                            </Button>
                        )}
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="envoyer-devis-btn"
                            disabled={sending || activeDevisFormules.length === 0 || !canWriteDevis}
                            title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : ''}
                            onClick={openSendModal}>
                            {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {sending ? 'Envoi...' : 'Envoyer'}
                        </Button>
                    </div>
                </div>

                {/* Important Notes Banner */}
                <ImportantNotesBanner prospectId={devis.prospect_id} />

                {/* Section 1: Statut du devis */}
                <div className={`bg-blue-50 rounded-xl border-2 ${isEnvoye ? 'border-emerald-400' : 'border-amber-300'} p-5`} data-testid="section-statut">
                    <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-base font-bold text-[#1B365D]">1. Statut du devis</h2>
                    </div>
                    {isEnvoye ? (
                        <div className="flex items-center gap-4">
                            <div className="relative" data-testid="statut-select">
                                <button
                                    onClick={() => canChangeStatutDevis ? setStatutDropOpen(!statutDropOpen) : null}
                                    disabled={!canChangeStatutDevis}
                                    title={canChangeStatutDevis ? '' : "Vous n'avez pas la permission de changer le statut"}
                                    className={`flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold bg-white shadow-sm focus:outline-none transition-all min-w-[180px] ${canChangeStatutDevis ? 'hover:shadow-md hover:border-[#1B365D]/30 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                                >
                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sCfg.dot || 'bg-gray-400'}`} />
                                    <span className="text-[#1B365D] flex-1 text-left">{sCfg.label}</span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${statutDropOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {statutDropOpen && (
                                    <>
                                        <div className="fixed inset-0 z-30" onClick={() => setStatutDropOpen(false)} />
                                        <div className="absolute left-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[220px] max-h-[320px] overflow-y-auto">
                                            {Object.entries(availableStatuts).map(([key, cfg]) => (
                                                <button key={key} onClick={() => handleStatutChange(key)}
                                                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                                                        devis.statut_devis === key ? 'bg-[#1B365D]/5 font-semibold' : 'hover:bg-gray-50'
                                                    }`}>
                                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot || 'bg-gray-400'}`} />
                                                    <span className="text-gray-700">{cfg.label}</span>
                                                    {devis.statut_devis === key && <span className="ml-auto text-[#1B365D]">&#10003;</span>}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            <Badge className={`${sCfg.color} border text-sm px-3 py-1.5 font-semibold`}>{sCfg.label}</Badge>
                            <span className="text-sm text-gray-400">({activeDevisFormules.length} devis actifs)</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <Badge className={`${sCfg.color} border text-sm px-3 py-1`}>{sCfg.label}</Badge>
                            <span className="text-sm text-gray-500 italic">{sCfg.hint}</span>
                            {activeDevisFormules.length > 0 && (
                                <span className="text-sm text-gray-500">({activeDevisFormules.length} devis actifs)</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Section 2: Contact client */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="section-contact">
                    <div className="flex items-center gap-2 mb-5">
                        <User className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-base font-bold text-[#1B365D]">2. Contact client</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-6 mb-5">
                        <div>
                            <span className="text-xs text-red-400 font-medium">Nom</span>
                            <p className="text-sm font-semibold text-[#1B365D] mt-0.5">{prospect.nom || '-'}</p>
                        </div>
                        <div>
                            <span className="text-xs text-red-400 font-medium">Telephone</span>
                            <p className="text-sm font-semibold text-[#1B365D] mt-0.5">{prospect.telephone || '-'}</p>
                        </div>
                        <div>
                            <span className="text-xs text-red-400 font-medium">Email</span>
                            <p className="text-sm font-semibold text-[#1B365D] mt-0.5">{prospect.email || '-'}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-6 mb-5">
                        <div>
                            <span className="text-xs text-gray-400 font-medium">Commercial</span>
                            <p className="text-sm text-gray-500 mt-0.5 italic">{devis.commercial_name || devis.inventaire?.commercial_name || 'Non assigne'}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-400 font-medium">Type</span>
                            <p className="text-sm text-[#1B365D] mt-0.5">{prospect.statut?.replace(/_/g, ' ') || '-'}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-400 font-medium">Source & Departement</span>
                            <p className="text-sm text-[#1B365D] mt-0.5">
                                {[prospect.source, prospect.departement].filter(Boolean).join(' — ') || '-'}
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-0 items-stretch border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 bg-gray-50/50">
                            <div className="flex items-center gap-1.5 mb-2">
                                <MapPin className="w-3.5 h-3.5 text-green-500" />
                                <span className="text-xs font-bold text-green-600 uppercase tracking-wider">Depart</span>
                            </div>
                            <p className="text-sm text-[#1B365D]">
                                {getAdresseText(adresseDepart) || <span className="text-gray-400 italic">Non renseignee</span>}
                            </p>
                        </div>
                        <div className="flex items-center px-3 text-blue-400">
                            <span className="text-lg">— — &#9654;</span>
                        </div>
                        <div className="p-4 bg-gray-50/50">
                            <div className="flex items-center gap-1.5 mb-2">
                                <MapPin className="w-3.5 h-3.5 text-red-500" />
                                <span className="text-xs font-bold text-red-500 uppercase tracking-wider">Arrivee</span>
                            </div>
                            <p className="text-sm text-[#1B365D]">
                                {getAdresseText(adresseArrivee) || <span className="text-gray-400 italic">Non renseignee</span>}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section 3+: Formules */}
                {formules.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center" data-testid="section-no-formules">
                        <p className="text-gray-400 italic">
                            Aucune formule selectionnee dans l'inventaire. Veuillez d'abord selectionner des formules.
                        </p>
                    </div>
                ) : (
                    formules.map((f, idx) => {
                        const label = FORMULE_LABELS[f] || f;
                        const formuleDevis = (devis.devis_formules || []).filter(df => df.formule === f);
                        const activeDevis = formuleDevis.filter(df => !df.is_archived);
                        const archivedDevis = formuleDevis.filter(df => df.is_archived);
                        const activeCount = activeDevis.length;
                        return (
                            <div key={f} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid={`section-formule-${f}`}>
                                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                                    <FileText className="w-4 h-4 text-[#1B365D]" />
                                    <h2 className="text-base font-bold text-[#1B365D]">{idx + 3}. {label}</h2>
                                    <Badge variant="outline" className={`text-xs ${formuleDevis.length > 0 ? 'border-blue-300 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-500'}`}>
                                        {formuleDevis.length} devis
                                    </Badge>
                                </div>

                                {/* Active devis (current version) - green border */}
                                {activeDevis.map((df) => (
                                    <div key={df.id} className="border-2 border-green-300 bg-green-50/30 rounded-lg p-4 mb-3">
                                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-4 items-center">
                                            <div>
                                                <span className="text-xs text-red-400 font-medium">N. Devis</span>
                                                <p className="text-sm font-bold text-[#1B365D] mt-0.5">{df.numero}</p>
                                                {df.version && <p className="text-xs text-gray-400 mt-0.5">v{df.version}</p>}
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 font-medium">Cree le</span>
                                                <p className="text-sm text-gray-700 mt-0.5">
                                                    {df.created_at ? new Date(df.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 font-medium">Volume</span>
                                                <p className="text-sm text-gray-700 mt-0.5">{df.volume != null ? `${df.volume} m\u00b3` : '- m\u00b3'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 font-medium">Distance</span>
                                                <p className="text-sm text-gray-700 mt-0.5">{df.distance_km != null ? `${df.distance_km} km` : '- km'}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xs text-gray-400 font-medium">Prix</span>
                                                <p className="text-base font-bold text-[#1B365D] mt-0.5">{df.total_ttc?.toFixed(2)} &#8364;</p>
                                            </div>
                                            <div className="relative">
                                                <button onClick={() => setOpenMenuId(openMenuId === df.id ? null : df.id)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-[#1B365D]"
                                                    data-testid={`devis-menu-${df.id}`}>
                                                    <MoreHorizontal className="w-5 h-5" />
                                                </button>
                                                {openMenuId === df.id && (
                                                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 w-40">
                                                        <button onClick={() => openViewModal(df)} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                            <Eye className="w-4 h-4 text-gray-400" /> Voir
                                                        </button>
                                                        <button
                                                            onClick={() => openEditModal(df)}
                                                            disabled={!canWriteDevis}
                                                            title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : ''}
                                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                                            data-testid={`devis-edit-active-${df.id}`}>
                                                            <Pencil className="w-4 h-4 text-gray-400" /> Modifier
                                                        </button>
                                                        <button onClick={async () => { 
                                                            setOpenMenuId(null); 
                                                            try {
                                                                const res = await axios.get(`${API_URL}/api/devis-formules/${df.id}/pdf`, { withCredentials: true, responseType: 'blob' });
                                                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                                const a = document.createElement('a'); a.href = url; a.download = `devis_${df.numero}.pdf`; a.click();
                                                                window.URL.revokeObjectURL(url);
                                                            } catch { toast.error('Erreur lors du telechargement'); }
                                                        }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                            <Download className="w-4 h-4 text-gray-400" /> Telecharger
                                                        </button>
                                                        {canDeleteDevis && (
                                                            <button
                                                                onClick={async () => {
                                                                    setOpenMenuId(null);
                                                                    if (!window.confirm(`Supprimer le devis ${df.numero} ? Cette action peut etre annulee depuis la Corbeille.`)) return;
                                                                    try {
                                                                        await axios.delete(`${API_URL}/api/devis-formules/${df.id}`, { withCredentials: true });
                                                                        toast.success('Devis supprime');
                                                                        fetchDevis();
                                                                    } catch (err) {
                                                                        toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression');
                                                                    }
                                                                }}
                                                                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-gray-100"
                                                                data-testid={`devis-delete-${df.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4 text-red-500" /> Supprimer
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Archived devis (history) - grey, read-only */}
                                {archivedDevis.map((df) => (
                                    <div key={df.id} className="border border-gray-200 bg-gray-50/50 rounded-lg p-4 mb-3 opacity-70">
                                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-4 items-center">
                                            <div>
                                                <span className="text-xs text-red-400 font-medium">N. Devis</span>
                                                <p className="text-sm font-bold text-gray-500 mt-0.5 line-through">{df.numero}</p>
                                                {df.archived_at && (
                                                    <p className="text-[10px] text-blue-500 mt-1 leading-tight">
                                                        Modifie le {new Date(df.archived_at).toLocaleDateString('fr-FR')} a {new Date(df.archived_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} pour creer le devis {df.replaced_by_numero}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 font-medium">Cree le</span>
                                                <p className="text-sm text-gray-500 mt-0.5">
                                                    {df.created_at ? new Date(df.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 font-medium">Volume</span>
                                                <p className="text-sm text-gray-500 mt-0.5">{df.volume != null ? `${df.volume} m\u00b3` : '- m\u00b3'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 font-medium">Distance</span>
                                                <p className="text-sm text-gray-500 mt-0.5">{df.distance_km != null ? `${df.distance_km} km` : '- km'}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-xs text-gray-400 font-medium">Prix</span>
                                                <p className="text-base font-medium text-gray-500 mt-0.5">{df.total_ttc?.toFixed(2)} &#8364;</p>
                                            </div>
                                            <div className="relative">
                                                <button onClick={() => setOpenMenuId(openMenuId === df.id ? null : df.id)}
                                                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400"
                                                    data-testid={`devis-menu-${df.id}`}>
                                                    <MoreHorizontal className="w-5 h-5" />
                                                </button>
                                                {openMenuId === df.id && (
                                                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 w-40">
                                                        <button onClick={() => openViewModal(df)} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                            <Eye className="w-4 h-4 text-gray-400" /> Voir
                                                        </button>
                                                        <button
                                                            onClick={() => openEditModal(df)}
                                                            disabled={!canWriteDevis}
                                                            title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : ''}
                                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                                            data-testid={`devis-edit-${df.id}`}>
                                                            <Pencil className="w-4 h-4 text-gray-400" /> Modifier
                                                        </button>
                                                        <button onClick={async () => { 
                                                            setOpenMenuId(null); 
                                                            try {
                                                                const res = await axios.get(`${API_URL}/api/devis-formules/${df.id}/pdf`, { withCredentials: true, responseType: 'blob' });
                                                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                                const a = document.createElement('a'); a.href = url; a.download = `devis_${df.numero}.pdf`; a.click();
                                                                window.URL.revokeObjectURL(url);
                                                            } catch { toast.error('Erreur lors du telechargement'); }
                                                        }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                            <Download className="w-4 h-4 text-gray-400" /> Telecharger
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Empty state or add more */}
                                {formuleDevis.length === 0 && (
                                    <p className="text-gray-400 italic mb-4 text-center py-4">Aucun devis pour cette formule</p>
                                )}
                                <div className="text-center">
                                    <Button variant="outline" className="gap-2 text-sm border-gray-300 text-[#1B365D] hover:bg-[#1B365D]/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={!canWriteDevis}
                                        title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : ''}
                                        onClick={() => openFormuleModal(f)}
                                        data-testid={`create-devis-formule-${f}`}>
                                        <Plus className="w-4 h-4" />
                                        Creer un devis {label}
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                )}

                {/* Historique des relances */}
                {Array.isArray(devis.relance_history) && devis.relance_history.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-amber-200 border-l-4 border-l-amber-400 overflow-hidden" data-testid="devis-relances-history">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <svg className="w-5 h-5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                <h2 className="text-base font-bold text-[#1B365D]">Historique des relances</h2>
                            </div>
                            <span className="text-xs text-gray-400">{devis.relance_history.length} entrée{devis.relance_history.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {[...devis.relance_history]
                                .filter(r => r.remark || r.refuse_reason || r.next_relance_date)
                                .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                                .map((r, idx) => {
                                    const cfg = STATUT_CONFIG[r.statut] || { label: r.statut, color: 'bg-gray-100 text-gray-700 border-gray-300' };
                                    const text = r.remark || r.refuse_reason || '';
                                    return (
                                        <div key={idx} className="p-5" data-testid={`devis-relance-entry-${idx}`}>
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <Badge className={`${cfg.color} border text-[11px] px-2 py-0.5`}>{cfg.label}</Badge>
                                                <span className="text-xs text-gray-400">
                                                    {r.date ? new Date(r.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                                                    {r.date ? ` · ${new Date(r.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                                </span>
                                                {r.by_name && (<span className="text-xs text-gray-400">par {r.by_name}</span>)}
                                            </div>
                                            {text && (<p className="text-sm text-[#1B365D] whitespace-pre-wrap break-words">« {text} »</p>)}
                                            {r.next_relance_date && (
                                                <p className="text-[11px] text-blue-700 mt-1.5">
                                                    Prochaine relance prévue le {new Date(r.next_relance_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </p>
                                            )}
                                            {r.justificatif?.url && (
                                                <a
                                                    href={`${API_URL}${r.justificatif.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 mt-2 text-[11px] text-[#1B365D] bg-amber-50 border border-amber-200 px-2 py-1 rounded hover:bg-amber-100"
                                                    data-testid={`devis-relance-justif-${idx}`}
                                                >
                                                    <FileText className="w-3 h-3" />
                                                    {r.justificatif.file_name || 'Justificatif'}
                                                </a>
                                            )}
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                )}
            </div>

            {/* Create Devis Modal */}
            <DevisFormuleModal
                open={modalOpen}
                onClose={(shouldRefresh) => {
                    setModalOpen(false);
                    if (shouldRefresh) fetchDevis();
                }}
                formule={selectedFormule}
                prospect={{ ...prospect, date_chargement: devis.date_chargement, date_dechargement: devis.prospect?.date_dechargement }}
                devisId={id}
                volumeItems={devis.volume_items || []}
                mode={modalMode}
                existingData={modalExistingData}
                existingId={modalExistingId}
                otherFormules={formules}
            />

            {/* Send Devis Modal */}
            <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
                <DialogContent className="max-w-lg p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="send-devis-modal">
                    <DialogTitle className="sr-only">Envoyer les devis par email</DialogTitle>
                    <div className="p-5 space-y-3.5 overflow-y-auto flex-1">
                        {/* Header */}
                        <div className="flex items-center gap-2">
                            <Send className="w-5 h-5 text-gray-600" />
                            <h2 className="text-lg font-bold text-gray-900">Envoyer les devis par email</h2>
                        </div>

                        {/* Devis selection */}
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-semibold text-gray-700">Devis a joindre ({sendSelected.length}/{activeDevisFormules.length})</span>
                            </div>
                            <div className="border border-gray-200 rounded-lg max-h-32 overflow-y-auto divide-y divide-gray-100">
                                {activeDevisFormules.map(df => (
                                    <label key={df.id} className="flex items-center justify-between px-3 py-1.5 hover:bg-gray-50 cursor-pointer" data-testid={`send-check-${df.id}`}>
                                        <div className="flex items-center gap-3">
                                            <input type="checkbox" checked={sendSelected.includes(df.id)}
                                                onChange={() => toggleSendSelection(df.id)}
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                            <span className="text-sm font-medium text-gray-800">{df.numero}</span>
                                            <Badge className="text-xs" style={{ background: '#E8F0FE', color: '#1B365D', border: '1px solid #BDD3F0' }}>
                                                {FORMULE_LABELS[df.formule] || df.formule}
                                            </Badge>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-700">{df.total_ttc?.toFixed(2)} EUR</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Civilite + Destinataire */}
                        <div className="flex gap-3">
                            <div className="w-28">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Civilite</label>
                                <select value={sendCivilite} onChange={e => {
                                    setSendCivilite(e.target.value);
                                    applyTemplate(sendTemplate, e.target.value, devis?.nom || '');
                                }}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="send-civilite">
                                    <option value="M.">M.</option>
                                    <option value="Mme">Mme</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Destinataire</label>
                                <Input value={sendEmail} onChange={e => setSendEmail(e.target.value)}
                                    placeholder="email@exemple.com" data-testid="send-email" />
                            </div>
                        </div>

                        {/* Template selector - compact */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Type de message</label>
                            <div className="grid grid-cols-3 gap-2" data-testid="send-template-selector">
                                {Object.entries(MAIL_TEMPLATES).map(([key, tpl]) => {
                                    const active = sendTemplate === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => handleTemplateChange(key)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${
                                                active
                                                    ? `${tpl.color} ring-2 ring-offset-1 ring-[#1B365D]/30`
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                            }`}
                                            data-testid={`send-template-${key}`}
                                        >
                                            {tpl.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Sujet */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Sujet</label>
                            <Input value={sendSubject} onChange={e => setSendSubject(e.target.value)}
                                data-testid="send-subject" />
                        </div>

                        {/* Message */}
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Message</label>
                            <Textarea value={sendMessage} onChange={e => setSendMessage(e.target.value)}
                                rows={8} className="resize-y text-sm" data-testid="send-message" />
                        </div>

                        {/* Attachments (settings files) */}
                        {settingsFiles.length > 0 && (
                            <div data-testid="send-attachments-block">
                                <label className="text-sm font-semibold text-gray-700 mb-2 block">Pièces jointes additionnelles</label>
                                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-gray-50/30">
                                    {settingsFiles.map(f => {
                                        const checked = sendAttachFileIds.includes(f.id);
                                        return (
                                            <label key={f.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white cursor-pointer text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        setSendAttachFileIds(prev => e.target.checked
                                                            ? [...prev, f.id]
                                                            : prev.filter(x => x !== f.id));
                                                    }}
                                                    className="w-4 h-4 accent-[#1B365D]"
                                                    data-testid={`send-attach-${f.id}`}
                                                />
                                                <FileText className="w-4 h-4 text-[#1B365D] shrink-0" />
                                                <span className="flex-1 truncate">{f.label}</span>
                                                <span className="text-xs text-gray-400 shrink-0">{f.original_name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer (sticky) */}
                    <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-200 shrink-0">
                        <span className="text-xs text-blue-600 italic" data-testid="send-attachment-count">
                            {sendSelected.length} devis PDF{sendAttachFileIds.length > 0 ? ` + ${sendAttachFileIds.length} pièce(s) jointe(s)` : ''}
                        </span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setSendModalOpen(false)} data-testid="send-cancel">Annuler</Button>
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                                disabled={sending || !sendSelected.length || !sendEmail}
                                onClick={handleSendMultiple} data-testid="send-confirm">
                                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {sending ? 'Envoi...' : 'Envoyer'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Relance email — civility selector */}
            <Dialog open={relanceEmailDialogOpen} onOpenChange={setRelanceEmailDialogOpen}>
                <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="relance-email-modal">
                    <DialogTitle className="sr-only">Envoyer un email de relance</DialogTitle>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6">
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                                <Mail className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">Relance par mail</h2>
                                <p className="text-orange-100 text-sm">Sélectionnez la civilité du client</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6 space-y-5">
                        <div>
                            <p className="text-sm text-gray-600 mb-3">
                                Email destinataire : <span className="font-semibold text-[#1B365D]">{devis.email || '—'}</span>
                            </p>
                            <p className="text-sm text-gray-600 mb-2">Civilité</p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setRelanceCivilite('Mme')}
                                    className={`rounded-xl border-2 py-3 font-semibold transition-all ${relanceCivilite === 'Mme' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`}
                                    data-testid="relance-civ-mme"
                                >
                                    Mme
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRelanceCivilite('Mr')}
                                    className={`rounded-xl border-2 py-3 font-semibold transition-all ${relanceCivilite === 'Mr' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`}
                                    data-testid="relance-civ-mr"
                                >
                                    Mr
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                                Prévisualisation : <span className="italic">"Bonjour {relanceCivilite} {devis.nom || 'Client'},"</span>
                            </p>
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setRelanceEmailDialogOpen(false)}
                                disabled={sendingRelanceEmail}
                                data-testid="relance-cancel-btn"
                            >
                                Annuler
                            </Button>
                            <Button
                                className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
                                onClick={confirmSendRelanceEmail}
                                disabled={sendingRelanceEmail}
                                data-testid="relance-confirm-btn"
                            >
                                {sendingRelanceEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {sendingRelanceEmail ? 'Envoi...' : 'Envoyer'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Relance email — success */}
            <Dialog open={relanceSuccessOpen} onOpenChange={setRelanceSuccessOpen}>
                <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="relance-success-modal">
                    <DialogTitle className="sr-only">Email envoyé</DialogTitle>
                    <div className="p-8 text-center">
                        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg animate-bounce">
                            <PartyPopper className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#1B365D] mt-5">Bravo !</h2>
                        <p className="text-gray-600 mt-2">Le mail est envoyé 🎉</p>
                        <Button
                            className="mt-6 bg-[#1B365D] hover:bg-[#2A4A7A] text-white px-8"
                            onClick={() => setRelanceSuccessOpen(false)}
                            data-testid="relance-success-ok"
                        >
                            OK
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Accept Devis Modal */}
            <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
                <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="accept-devis-modal">
                    <DialogTitle className="sr-only">Accepter un devis</DialogTitle>
                    <div className="p-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                            <h2 className="text-lg font-bold text-gray-900">Accepter un devis</h2>
                        </div>
                        <p className="text-sm text-gray-500">Choisissez le devis accepte par le client. Les autres devis actifs seront automatiquement refuses.</p>

                        <div className="space-y-3">
                            {activeDevisFormules.map(df => (
                                <label key={df.id}
                                    className={`flex items-start gap-4 border rounded-xl p-4 cursor-pointer transition-all ${
                                        acceptSelectedDf === df.id ? 'border-emerald-400 bg-emerald-50/50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    data-testid={`accept-radio-${df.id}`}>
                                    <input type="radio" name="accept_df" checked={acceptSelectedDf === df.id}
                                        onChange={() => setAcceptSelectedDf(df.id)}
                                        className="mt-1 w-5 h-5 text-emerald-600 border-gray-300 focus:ring-emerald-500" />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-gray-800">{df.numero}</span>
                                            <Badge className="text-xs" style={{ background: '#E8F0FE', color: '#1B365D', border: '1px solid #BDD3F0' }}>
                                                {FORMULE_LABELS[df.formule] || df.formule}
                                            </Badge>
                                        </div>
                                        <span className="text-sm text-gray-500">{df.total_ttc?.toFixed(2)} EUR</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <Button variant="outline" onClick={() => setAcceptModalOpen(false)} data-testid="accept-cancel">Annuler</Button>
                        <Button className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={accepting || !acceptSelectedDf || !canWriteDevis}
                            title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : ''}
                            onClick={handleAcceptDevis} data-testid="accept-confirm">
                            {accepting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            {accepting ? 'En cours...' : 'Accepter ce devis'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Refuse Modal */}
            <Dialog open={refuseModalOpen} onOpenChange={setRefuseModalOpen}>
                <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="refuse-devis-modal">
                    <DialogTitle className="sr-only">Refuser le devis</DialogTitle>
                    <div className="p-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <XCircle className="w-6 h-6 text-red-500" />
                            <h2 className="text-lg font-bold text-gray-900">Refuser le devis</h2>
                        </div>
                        <p className="text-sm text-gray-500">Veuillez indiquer la raison du refus. Ce champ est obligatoire.</p>
                        <div>
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Raison du refus</label>
                            <Textarea value={refuseReason} onChange={e => setRefuseReason(e.target.value)}
                                rows={4} placeholder="Ex: Budget trop eleve, a trouve un autre prestataire..."
                                className="resize-none" data-testid="refuse-reason" />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <Button variant="outline" onClick={() => setRefuseModalOpen(false)} data-testid="refuse-cancel">Annuler</Button>
                        <Button className="bg-red-500 hover:bg-red-600 text-white gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={refusing || !refuseReason.trim() || !canWriteDevis}
                            title={!canWriteDevis ? 'Accès refusé : permission Écriture requise' : ''}
                            onClick={handleRefuse} data-testid="refuse-confirm">
                            {refusing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                            {refusing ? 'En cours...' : 'Valider le refus'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Relance 1 Modal */}
            <Dialog open={relanceModalOpen} onOpenChange={(v) => { if (!savingRelance) setRelanceModalOpen(v); }}>
                <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="relance1-modal">
                    <DialogTitle className="sr-only">Relance 1</DialogTitle>
                    <div className="p-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <svg className="w-6 h-6 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            <h2 className="text-lg font-bold text-gray-900">{
                                pendingRelanceStatus === 'relance_whatsapp' ? 'Relance WhatsApp' :
                                pendingRelanceStatus === 'relance_3' ? 'Relance 3' :
                                pendingRelanceStatus === 'relance_2' ? 'Relance 2' : 'Relance 1'
                            } — Remarque</h2>
                        </div>
                        <p className="text-sm text-gray-500">{
                            pendingRelanceStatus === 'relance_whatsapp'
                                ? "Indiquez la teneur du dernier contact WhatsApp avec le client."
                                : `Indiquez si le client a répondu (ou non) et planifiez la ${
                                    pendingRelanceStatus === 'relance_3' ? 'Relance WhatsApp' :
                                    pendingRelanceStatus === 'relance_2' ? 'Relance 3' : 'Relance 2'
                                  }.`
                        }</p>
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 block">Remarque</label>
                            <Textarea
                                value={relanceRemark}
                                onChange={e => setRelanceRemark(e.target.value)}
                                rows={3}
                                placeholder={pendingRelanceStatus === 'relance_whatsapp'
                                    ? "Ex : Message WhatsApp envoyé, en attente de retour / lu sans réponse..."
                                    : "Ex : Client injoignable, laissé message vocal / demande de rappeler demain..."}
                                className="resize-none"
                                data-testid="relance-remark"
                                autoFocus
                            />
                        </div>
                        {pendingRelanceStatus !== 'relance_whatsapp' && (
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 block">Date {
                                pendingRelanceStatus === 'relance_3' ? 'Relance WhatsApp' :
                                pendingRelanceStatus === 'relance_2' ? 'Relance 3' : 'Relance 2'
                            }</label>
                            <Input
                                type="date"
                                value={relanceNextDate}
                                onChange={e => setRelanceNextDate(e.target.value)}
                                className="w-full"
                                data-testid="relance-next-date"
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                        )}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 block">
                                Justificatif <span className="text-xs font-normal text-gray-400">(facultatif – photo ou PDF)</span>
                            </label>
                            <label className="flex flex-col items-center gap-1 border-2 border-dashed border-gray-300 rounded-lg px-3 py-3 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="relance-justif-dropzone">
                                <Upload className="w-5 h-5 text-gray-400" />
                                <span className="text-xs text-gray-600 text-center">
                                    {relanceJustifFile ? relanceJustifFile.name : 'Cliquez pour ajouter une photo ou un fichier'}
                                </span>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*,.pdf"
                                    onChange={e => setRelanceJustifFile(e.target.files?.[0] || null)}
                                    data-testid="relance-justif-input"
                                />
                            </label>
                            {relanceJustifFile && (
                                <button
                                    type="button"
                                    onClick={() => setRelanceJustifFile(null)}
                                    className="text-xs text-red-600 hover:underline"
                                    data-testid="relance-justif-clear"
                                >
                                    Retirer le fichier
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <Button variant="outline" onClick={() => setRelanceModalOpen(false)} disabled={savingRelance} data-testid="relance-cancel">Annuler</Button>
                        <Button
                            className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                            disabled={savingRelance || relanceJustifUploading || !relanceRemark.trim() || (pendingRelanceStatus !== 'relance_whatsapp' && !relanceNextDate)}
                            onClick={handleRelance1Confirm}
                            data-testid="relance-confirm"
                        >
                            {relanceJustifUploading ? 'Upload…' : (savingRelance ? 'Enregistrement…' : 'Valider')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Success Dialog after sending devis email */}
            <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
                <DialogContent
                    className="max-w-md text-center p-8"
                    data-testid="send-devis-success-dialog"
                >
                    <DialogTitle className="sr-only">Email envoyé</DialogTitle>
                    <div className="flex flex-col items-center gap-4">
                        <div className="text-6xl animate-bounce" aria-hidden="true">🎉</div>
                        <h2
                            className="text-2xl font-bold text-[#1B365D]"
                            data-testid="send-devis-success-title"
                        >
                            Félicitations !
                        </h2>
                        <p className="text-base text-gray-700">
                            Le mail est envoyé <span aria-hidden="true">👏</span>
                        </p>
                        <Button
                            className="mt-2 bg-[#1B365D] hover:bg-[#15294a] text-white px-8"
                            onClick={() => setSuccessDialogOpen(false)}
                            data-testid="send-devis-success-close"
                        >
                            Super !
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
